import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';

export interface ContinuationParam {
  today: string;
  search_treatment: string;
  search_treatement_org: string;
  search_treatment_yearmonth?: string | null;
  search_treatement_from: string;
  search_treatement_to: string;
  search_term_type: number;
  patient_cost_rate?: number | string | null;
  bui?: number | string | null;
  shoken1?: number;
  shoken2?: number;
  search_text?: number;
  search_patient_id?: string;
  search_patient_name?: string;
  ini?: string;
  rezept_ids_para: string;
  sort: number;
  is_init: number;
  option_1?: number;
  option_2?: number;
}

export interface FreeMedicalItem {
  id: number;
  name: string;
  price: number;
  category_name: string;
  type_name: string;
}

export interface ContinuationOptionItem {
  id: number | string;
  name: string;
}

export interface ContinuationInitResponse {
  success: boolean;
  data: {
    param: ContinuationParam;
    free_medicals: FreeMedicalItem[];
    default_flag: number;
    iniarr?: ContinuationOptionItem[];
    ratearr?: ContinuationOptionItem[];
  };
}

export interface ContinuationListRow {
  id: number;
  rezept_id?: number;
  rezept_id2?: number;
  patient_id: number;
  massager_id?: number;
  serial_number: string | number;
  name?: string;
  name_kana?: string;
  insurer_kind_name?: string;
  patient_cost_rate?: number;
  patient_cost_rate_str?: string;
  medical_aid_name?: string;
  is_initial_inspection_fee_mark?: string;
  print_invoice_mark?: string;
  visit_datetime_from?: string;
  is_locked?: number | boolean;
  free_medical_treatment_name?: string;
  free_medical_treatment_count?: string;
  free_medical_treatment_fee?: string;
  madoguchi_price?: number;
  treatment_price?: number;
  patient_price?: number;
  move_to_visit_clinic_month?: string;
  is_report_only?: boolean;
  [key: string]: unknown;
}

export interface ContinuationListPagination {
  total: number;
  current_page: number;
  per_page: number;
  last_page: number;
}

export interface ContinuationListTotals {
  madoguchi: number;
  treatment: number;
  patient: number;
}

export interface ContinuationListResponse {
  success: boolean;
  data: {
    param: ContinuationParam;
    free_medicals: FreeMedicalItem[];
    default_flag: number;
    iniarr?: ContinuationOptionItem[];
    ratearr?: ContinuationOptionItem[];
    list: ContinuationListRow[];
    pagination?: ContinuationListPagination;
    totals?: ContinuationListTotals;
  };
}

/**
 * GET /patients/continuation/init
 */
export async function fetchContinuationInit(
  siteType: string,
  clinicNumber: string
): Promise<ContinuationInitResponse['data']> {
  const res = await apiClient.get<ContinuationInitResponse>(
    clinicApiPath(siteType, clinicNumber, '/patients/continuation/init')
  );
  return res.data.data;
}

/**
 * POST /patients/continuation/list
 */
export async function fetchContinuationList(
  siteType: string,
  clinicNumber: string,
  params: Record<string, unknown>
): Promise<ContinuationListResponse['data']> {
  const res = await apiClient.post<ContinuationListResponse>(
    clinicApiPath(siteType, clinicNumber, '/patients/continuation/list'),
    params
  );
  return res.data.data;
}

/**
 * POST /patients/continuation/list/delete
 */
export async function deleteContinuation(
  siteType: string,
  clinicNumber: string,
  payload: { delete_id?: number; visit_ids?: number[] }
): Promise<{ response: boolean }> {
  const res = await apiClient.post<{ success: boolean; data: { response: boolean } }>(
    clinicApiPath(siteType, clinicNumber, '/patients/continuation/list/delete'),
    payload
  );
  return res.data.data;
}

/**
 * POST /patients/continuation/r2-list/delete — R2 counterpart of deleteContinuation (r_visits).
 */
export async function deleteR2Continuation(
  siteType: string,
  clinicNumber: string,
  payload: { delete_id?: number; visit_ids?: number[] }
): Promise<{ response: boolean }> {
  const res = await apiClient.post<{ success: boolean; data: { response: boolean } }>(
    clinicApiPath(siteType, clinicNumber, '/patients/continuation/r2-list/delete'),
    payload
  );
  return res.data.data;
}

/**
 * GET /patients/continuation/search-id/{sKeyword}
 */
export async function searchContinuationPatientId(
  siteType: string,
  clinicNumber: string,
  sKeyword: string
): Promise<{ response: boolean; patient_id?: number }> {
  const res = await apiClient.get<{
    success: boolean;
    data: { response: boolean; patient_id?: number };
  }>(
    clinicApiPath(
      siteType,
      clinicNumber,
      `/patients/continuation/search-id/${encodeURIComponent(sKeyword)}`
    )
  );
  return res.data.data;
}

/**
 * GET /patients/continuation/search/{sKeyword}/{searchTreatment}/{sortRadio}
 */
export interface SearchContinuationResult {
  patients_data_is: number;
  search_serial_number: number;
  s_patient_id: number | string;
  param: { s_keyword: string; patients_data: Array<Record<string, unknown>> };
  search_treatment: string;
  sort_ratio: string;
}

export async function searchContinuation(
  siteType: string,
  clinicNumber: string,
  sKeyword: string,
  searchTreatment: string,
  sortRadio: string
): Promise<SearchContinuationResult> {
  const res = await apiClient.get<{ success: boolean; data: SearchContinuationResult }>(
    clinicApiPath(
      siteType,
      clinicNumber,
      `/patients/continuation/search/${encodeURIComponent(sKeyword)}/${encodeURIComponent(searchTreatment)}/${encodeURIComponent(sortRadio)}`
    )
  );
  return res.data.data;
}

/**
 * GET /patients/continuation/edit-init/{patientId}/{searchYearmonth}
 * Schedule data for edit page: patient + visits + calendar data.
 */
export interface VisitDataByDay {
  id: number;
  patient_id: number;
  is_initial_inspection_fee: number;
  is_acupuncture: number;
  is_moxibustion: number;
  electric_treatment: number;
  outcome_type_id: number;
  massager_id: number | null;
  previous_visit_id: number;
  visit_datetime_from: string;
  is_error: number;
  is_locked: number;
}

export interface ConsentRow {
  id: number;
  serial_number: number | string;
  injury_name: string;
  consent_date: string | null;
  first_treatment_date: string | null;
  treatment_term_from: string | null;
  treatment_term_to: string | null;
  treatment_content: string;
}

export interface MassagerOption {
  id: number;
  serial_number: string | number;
  name: string;
  address1: string;
  address2: string;
  address3: string;
}

export interface RezeptPriceRow {
  id: number;
  relationship_id: number;
  insurance_type_id: number;
  insurer_number: string;
  patient_cost_rate: string;
  qualification_date: string;
  expiration_date: string;
  all_status: number;
  all_return: number;
  rezept_yearmonth: number | null;
  rezept_yearmonth_j: string;
  summary: string;
}

export interface PriceDataRow {
  initial: string;
  acupuncture: string;
  moxibustion: string;
  both: string;
  electric: string;
  house_call: string;
  house_call_excess: string;
  special_area_addition: string;
  initial_count: number;
  acupuncture_count: number;
  moxibustion_count: number;
  both_count: number;
  electric_count: number;
  house_call_count: number;
  house_call_excess_count: number;
  special_area_addition_count: number;
  operation_day_count: number;
  total_price: string;
}

export interface ContinuationEditInitData {
  patient: {
    id?: number;
    serial_number?: string | number;
    name?: string;
    name_kana?: string;
    [key: string]: unknown;
  };
  search_yearmonth: string;
  visits: ContinuationListRow[];
  visit_data_by_day: Record<string, VisitDataByDay>;
  consent_list: ConsentRow[];
  massager_list: MassagerOption[];
  outcome_types: { id: number; name: string }[];
  division: string;
  has_health_insurance: boolean;
  days_in_month: number;
  year_month_label: string;
  rezept_list: RezeptPriceRow[];
  price_data: Record<number, PriceDataRow>;
}

export async function fetchContinuationEditInit(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  searchYearmonth: string
): Promise<ContinuationEditInitData> {
  const res = await apiClient.get<{ success: boolean; data: ContinuationEditInitData }>(
    clinicApiPath(
      siteType,
      clinicNumber,
      `/patients/continuation/edit-init/${patientId}/${encodeURIComponent(searchYearmonth)}`
    )
  );
  return res.data.data;
}

/**
 * POST /patients/continuation/edit
 */
export async function fetchContinuationEdit(
  siteType: string,
  clinicNumber: string,
  visitId: number
): Promise<Record<string, unknown>> {
  const res = await apiClient.post<{ success: boolean; data: Record<string, unknown> }>(
    clinicApiPath(siteType, clinicNumber, '/patients/continuation/edit'),
    { visit_id: visitId }
  );
  return res.data.data;
}

/**
 * POST /patients/continuation/save
 */
export async function saveContinuation(
  siteType: string,
  clinicNumber: string,
  payload: Record<string, unknown>
): Promise<{ response: boolean; free_medical_treatment_fee?: number }> {
  const res = await apiClient.post<{
    success: boolean;
    data: { response: boolean; free_medical_treatment_fee?: number };
  }>(clinicApiPath(siteType, clinicNumber, '/patients/continuation/save'), payload);
  return res.data.data;
}

/**
 * POST /patients/continuation/print - Print receipt (modal print).
 */
export async function printContinuationModal(
  siteType: string,
  clinicNumber: string,
  payload: {
    visit_id: number;
    rezept_id?: number;
    is_free_medical?: number;
    print_invoice_save?: string;
  }
): Promise<{ response: boolean }> {
  const res = await apiClient.post<{ success: boolean; data: { response: boolean } }>(
    clinicApiPath(siteType, clinicNumber, '/patients/continuation/print'),
    payload
  );
  return res.data.data;
}

/**
 * POST /patients/continuation/print/pdf — Generate receipt PDF (領収書)
 */
export async function printContinuationReceiptPdf(
  siteType: string,
  clinicNumber: string,
  payload: Record<string, unknown>
): Promise<Blob> {
  const res = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/patients/continuation/print/pdf'),
    payload,
    { responseType: 'blob' }
  );
  return res.data as Blob;
}

/**
 * POST /patients/continuation/list/pdf
 */
export async function printContinuationListPdf(
  siteType: string,
  clinicNumber: string,
  payload: Record<string, unknown>
): Promise<Blob> {
  const res = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/patients/continuation/list/pdf'),
    payload,
    { responseType: 'blob' }
  );
  return res.data as Blob;
}

/**
 * GET /patients/continuation/print-reset/{visitId}
 */
export async function printResetContinuation(
  siteType: string,
  clinicNumber: string,
  visitId: number
): Promise<{ response: boolean }> {
  const res = await apiClient.get<{ success: boolean; data: { response: boolean } }>(
    clinicApiPath(siteType, clinicNumber, `/patients/continuation/print-reset/${visitId}`)
  );
  return res.data.data;
}

/**
 * GET /patients/continuation/change-price/{visitId}/{freeMedicalFee}
 */
export async function changePriceContinuation(
  siteType: string,
  clinicNumber: string,
  visitId: number,
  freeMedicalFee: number
): Promise<{ response: boolean; diff?: number }> {
  const res = await apiClient.get<{ success: boolean; data: { response: boolean; diff?: number } }>(
    clinicApiPath(
      siteType,
      clinicNumber,
      `/patients/continuation/change-price/${visitId}/${freeMedicalFee}`
    )
  );
  return res.data.data;
}

/**
 * POST /patients/continuation/schedule-update
 * Update schedule (登録): persist visit data for the month. visits = array of { id, is_initial_inspection_fee, ... }.
 */
export interface ScheduleUpdateVisit {
  id: number;
  is_initial_inspection_fee: number;
  is_acupuncture: number;
  is_moxibustion: number;
  electric_treatment: number;
  outcome_type_id: number;
  massager_id: number | null;
  visit_datetime_from: string;
  visit_datetime_to?: string;
  previous_visit_id: number;
}

export async function scheduleUpdateContinuation(
  siteType: string,
  clinicNumber: string,
  payload: { patient_id: number; yearmonth: string; visits: ScheduleUpdateVisit[] }
): Promise<{ response: boolean; updated: number }> {
  const res = await apiClient.post<{
    success: boolean;
    data: { response: boolean; updated: number };
  }>(clinicApiPath(siteType, clinicNumber, '/patients/continuation/schedule-update'), payload);
  return res.data.data;
}

/**
 * POST /patients/continuation/list/pdf
 * Generate PDF for continuation list. Returns blob.
 */
export async function downloadContinuationListPdf(
  siteType: string,
  clinicNumber: string,
  payload: {
    search_term_type: number;
    sort: number;
    visit_ids?: number[];
    search_treatment?: string;
    search_treatment_yearmonth?: string;
    search_treatement_from?: string;
    search_treatement_to?: string;
    patient_cost_rate?: number;
    bui?: number;
    shoken1?: number;
    shoken2?: number;
    print_receipt_100?: number;
    search_text?: number;
    search_patient_id?: string;
    search_patient_name?: string;
    ini?: string;
  },
): Promise<Blob> {
  const res = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/patients/continuation/list/pdf'),
    payload,
    { responseType: 'blob', headers: { Accept: 'application/pdf' } },
  );
  return res.data;
}

/**
 * GET /patients/continuation/add/{patientId}/{searchTreatment}/{sortRadio}
 * Add continuation (creates visit for the day). Throws on validation errors.
 */
export async function fetchContinuationAdd(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  searchTreatment: string,
  sortRadio: string
): Promise<ContinuationAddResult> {
  const res = await apiClient.get<{ success: boolean; data: ContinuationAddResult }>(
    clinicApiPath(
      siteType,
      clinicNumber,
      `/patients/continuation/add/${patientId}/${encodeURIComponent(searchTreatment)}/${encodeURIComponent(sortRadio)}`
    )
  );
  return res.data.data;
}

export interface ContinuationAddResult {
  response: boolean;
  patient_id: number;
  already_registered?: boolean;
}

/**
 * GET /patients/continuation/r2-add/{patientId}/{searchTreatment}/{sortRadio}
 * R2 counterpart of fetchContinuationAdd: creates the r_visits row for the day.
 */
export async function fetchR2ContinuationAdd(
  siteType: string,
  clinicNumber: string,
  patientId: number,
  searchTreatment: string,
  sortRadio: string
): Promise<ContinuationAddResult> {
  const res = await apiClient.get<{ success: boolean; data: ContinuationAddResult }>(
    clinicApiPath(
      siteType,
      clinicNumber,
      `/patients/continuation/r2-add/${patientId}/${encodeURIComponent(searchTreatment)}/${encodeURIComponent(sortRadio)}`
    )
  );
  return res.data.data;
}
